import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Download, Settings, Upload, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import CoveringEmailDialog from './CoveringEmailDialog';
import LastSentLine from './LastSentLine';
import ReconcileWarningBanner from './ReconcileWarningBanner';

// Feb 2026 — competition types where the Final Programme must NOT be
// generated until the Skating Order Draw has been completed. For
// International / World / Invitational comps the printed programme is
// traditionally finalised BEFORE the draw is run, so the Final
// Programme tile stays available without a draw.
const DRAW_REQUIRED_TYPES = ['Local', 'Regional', 'Area/State', 'National'];

/**
 * Self-contained panel for the Final Programme document. Owns its own state
 * for selected competition, programme settings, dialogs, and download flags.
 *
 * Props:
 *   competitionsWithOOS — array of {competition_id, name} fetched by parent.
 */
export default function FinalProgrammePanel({ competitionsWithOOS = [], competitionId = '' }) {
  const finalProgComp = competitionId;
  const [progSettings, setProgSettings] = useState(null);
  const [progDialogOpen, setProgDialogOpen] = useState(false);
  const [progSavingSettings, setProgSavingSettings] = useState(false);
  const [progDownloadingPrint, setProgDownloadingPrint] = useState(false);
  const [progDownloadingScreen, setProgDownloadingScreen] = useState(false);
  const [progUploadingCover, setProgUploadingCover] = useState(false);
  const [progNewVolName, setProgNewVolName] = useState('');
  const [progNewVolRole, setProgNewVolRole] = useState('');
  const [progSelectedVolIdx, setProgSelectedVolIdx] = useState(null);
  const [progNewSpName, setProgNewSpName] = useState('');
  const [progNewSpContact, setProgNewSpContact] = useState('');
  const [progSelectedSpIdx, setProgSelectedSpIdx] = useState(null);
  const [progGenDraftConfirmOpen, setProgGenDraftConfirmOpen] = useState(false);

  // Email tile state — multi-attachment selection
  const [clubsWithEntries, setClubsWithEntries] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailVersions, setEmailVersions] = useState({ print: true, screen: true });

  // Whether this competition has a published page — drives visibility of
  // the "Publish Programme PDFs to Competition Page" tile.
  const [publishedPageId, setPublishedPageId] = useState(null);
  const [publishingProgrammes, setPublishingProgrammes] = useState(false);
  const [lastPublishedAt, setLastPublishedAt] = useState(null);
  const [lastPublishTypes, setLastPublishTypes] = useState([]);

  // Draw gating — Final Programme actions are blocked for comp types
  // where the draw is a hard prerequisite (Local / Regional / Area /
  // National). For International / World / Invitational the programme
  // is finalised first, so the gate stays open.
  const [drawExists, setDrawExists] = useState(false);
  const compRecord = competitionsWithOOS.find(c => c.competition_id === finalProgComp);
  const compType = compRecord?.competition_type || null;
  const drawRequired = compType ? DRAW_REQUIRED_TYPES.includes(compType) : false;
  const drawBlocking = drawRequired && !drawExists;

  const fetchDrawState = async (compId) => {
    if (!compId) { setDrawExists(false); return; }
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${compId}/has-draw`,
        { withCredentials: true }
      );
      setDrawExists(!!res.data?.exists);
    } catch {
      setDrawExists(false);
    }
  };

  const fetchLastProgrammePublish = async (compId) => {
    if (!compId) { setLastPublishedAt(null); setLastPublishTypes([]); return; }
    try {
      const res = await axios.get(
        `${API}/publish/last-programme-publish/${compId}`,
        { withCredentials: true }
      );
      setLastPublishedAt(res.data?.last_published_at || null);
      setLastPublishTypes(res.data?.types_published || []);
    } catch {
      setLastPublishedAt(null);
      setLastPublishTypes([]);
    }
  };

  const fetchPublishedPage = async (compId) => {
    if (!compId) { setPublishedPageId(null); return; }
    try {
      const res = await axios.get(`${API}/competition-pages`, { withCredentials: true });
      const page = (res.data || []).find(p => p.competition_id === compId && p.status === 'approved');
      setPublishedPageId(page ? page.page_id : null);
    } catch {
      setPublishedPageId(null);
    }
  };

  const handlePublishProgrammes = async () => {
    if (!finalProgComp) return;
    setPublishingProgrammes(true);
    try {
      await Promise.all([
        axios.post(`${API}/documents/publish`,
          { competition_id: finalProgComp, document_type: 'programme_print' },
          { withCredentials: true, timeout: 180000 }
        ),
        axios.post(`${API}/documents/publish`,
          { competition_id: finalProgComp, document_type: 'programme_screen' },
          { withCredentials: true, timeout: 180000 }
        ),
      ]);
      toast.success('Both programme PDFs published to the Competition Page');
      // Refresh "Last published" line.
      fetchLastProgrammePublish(finalProgComp);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish programmes');
    } finally {
      setPublishingProgrammes(false);
    }
  };

  const fetchClubsWithEntries = async (compId) => {
    if (!compId) { setClubsWithEntries([]); return; }
    try {
      const res = await axios.get(
        `${API}/final-programme-email/${compId}/preflight`,
        { withCredentials: true }
      );
      setClubsWithEntries(res.data?.targets || []);
    } catch {
      setClubsWithEntries([]);
    }
  };

  const fetchProgSettings = async (compId) => {
    if (!compId) { setProgSettings(null); return; }
    try {
      const res = await axios.get(`${API}/programme-settings/${compId}`, { withCredentials: true });
      setProgSettings(res.data);
    } catch { setProgSettings(null); }
  };

  useEffect(() => {
    if (finalProgComp) {
      fetchProgSettings(finalProgComp);
      fetchClubsWithEntries(finalProgComp);
      fetchPublishedPage(finalProgComp);
      fetchLastProgrammePublish(finalProgComp);
      fetchDrawState(finalProgComp);
    } else {
      setProgSettings(null);
      setClubsWithEntries([]);
      setPublishedPageId(null);
      setLastPublishedAt(null);
      setLastPublishTypes([]);
      setDrawExists(false);
    }
    setSelectedClub('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalProgComp]);

  const updateProgSettings = (patch) => {
    setProgSettings(prev => ({ ...(prev || {}), ...patch }));
  };

  const saveProgSettings = async () => {
    if (!finalProgComp || !progSettings) return;
    setProgSavingSettings(true);
    try {
      const payload = {
        cover_type: progSettings.cover_type || 'text',
        cover_image_path: progSettings.cover_image_path || null,
        cover_image_filename: progSettings.cover_image_filename || null,
        include_clubs_list: progSettings.include_clubs_list ?? true,
        oaths_position: progSettings.oaths_position || 'front',
        skaters_oath_title: progSettings.skaters_oath_title || "Skater's Oath",
        skaters_oath_text: progSettings.skaters_oath_text || '',
        officials_oath_title: progSettings.officials_oath_title || "Officials' Oath",
        officials_oath_text: progSettings.officials_oath_text || '',
        volunteers: progSettings.volunteers || [],
        sponsors: progSettings.sponsors || [],
        page_header: progSettings.page_header || null,
        page_text: progSettings.page_text || null,
        sign_off: progSettings.sign_off || null,
      };
      await axios.put(`${API}/programme-settings/${finalProgComp}`, payload, { withCredentials: true });
      toast.success('Programme settings saved');
      setProgDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setProgSavingSettings(false);
    }
  };

  const uploadCoverImage = async (file) => {
    if (!finalProgComp || !file) return;
    setProgUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/programme-settings/${finalProgComp}/cover-image`,
        fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      updateProgSettings({
        cover_type: 'image',
        cover_image_path: res.data.path,
        cover_image_filename: res.data.filename,
      });
      toast.success('Cover image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setProgUploadingCover(false);
    }
  };

  const removeCoverImage = async () => {
    if (!finalProgComp) return;
    try {
      await axios.delete(`${API}/programme-settings/${finalProgComp}/cover-image`, { withCredentials: true });
      updateProgSettings({ cover_type: 'text', cover_image_path: null, cover_image_filename: null });
      toast.success('Cover image removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove image');
    }
  };

  const generateDraftAcknowledgements = async () => {
    if (!finalProgComp) return;
    try {
      const hasSponsors = (progSettings?.sponsors || []).length > 0;
      const res = await axios.get(
        `${API}/programme-settings/${finalProgComp}/default-acknowledgements`,
        { withCredentials: true, params: { has_sponsors: hasSponsors } }
      );
      updateProgSettings({
        page_header: res.data.page_header,
        page_text: res.data.page_text,
        sign_off: res.data.sign_off,
      });
      toast.success('Draft acknowledgements text inserted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate text');
    }
  };

  const handleGenerateDraftClick = () => {
    const hasContent = !!(progSettings?.page_header || progSettings?.page_text || progSettings?.sign_off);
    if (hasContent) setProgGenDraftConfirmOpen(true);
    else generateDraftAcknowledgements();
  };

  const addVolunteer = () => {
    if (!progNewVolName.trim()) return;
    const next = [...(progSettings?.volunteers || []), { name: progNewVolName.trim(), role: progNewVolRole.trim() }];
    updateProgSettings({ volunteers: next });
    setProgNewVolName(''); setProgNewVolRole('');
  };
  const updateVolunteer = () => {
    if (progSelectedVolIdx === null) return;
    const next = [...(progSettings?.volunteers || [])];
    next[progSelectedVolIdx] = { name: progNewVolName.trim(), role: progNewVolRole.trim() };
    updateProgSettings({ volunteers: next });
    setProgNewVolName(''); setProgNewVolRole(''); setProgSelectedVolIdx(null);
  };
  const deleteVolunteer = () => {
    if (progSelectedVolIdx === null) return;
    const next = (progSettings?.volunteers || []).filter((_, i) => i !== progSelectedVolIdx);
    updateProgSettings({ volunteers: next });
    setProgNewVolName(''); setProgNewVolRole(''); setProgSelectedVolIdx(null);
  };
  const selectVolunteer = (idx) => {
    const v = (progSettings?.volunteers || [])[idx];
    if (!v) return;
    setProgSelectedVolIdx(idx); setProgNewVolName(v.name || ''); setProgNewVolRole(v.role || '');
  };

  const addSponsor = () => {
    if (!progNewSpName.trim()) return;
    const next = [...(progSettings?.sponsors || []), { name: progNewSpName.trim(), contact: progNewSpContact.trim() }];
    updateProgSettings({ sponsors: next });
    setProgNewSpName(''); setProgNewSpContact('');
  };
  const updateSponsor = () => {
    if (progSelectedSpIdx === null) return;
    const next = [...(progSettings?.sponsors || [])];
    next[progSelectedSpIdx] = { name: progNewSpName.trim(), contact: progNewSpContact.trim() };
    updateProgSettings({ sponsors: next });
    setProgNewSpName(''); setProgNewSpContact(''); setProgSelectedSpIdx(null);
  };
  const deleteSponsor = () => {
    if (progSelectedSpIdx === null) return;
    const next = (progSettings?.sponsors || []).filter((_, i) => i !== progSelectedSpIdx);
    updateProgSettings({ sponsors: next });
    setProgNewSpName(''); setProgNewSpContact(''); setProgSelectedSpIdx(null);
  };
  const selectSponsor = (idx) => {
    const s = (progSettings?.sponsors || [])[idx];
    if (!s) return;
    setProgSelectedSpIdx(idx); setProgNewSpName(s.name || ''); setProgNewSpContact(s.contact || '');
  };

  const downloadFinalProgramme = async (mode) => {
    if (!finalProgComp) return;
    if (mode === 'print') setProgDownloadingPrint(true);
    else setProgDownloadingScreen(true);
    try {
      const url = mode === 'print'
        ? `${API}/order-of-skate/${finalProgComp}/final-programme`
        : `${API}/order-of-skate/${finalProgComp}/final-programme-screen`;
      const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      let filename = `Final_Programme_${mode}.docx`;
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Final Programme (${mode === 'print' ? 'Print' : 'Screen'}) downloaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate final programme');
    } finally {
      if (mode === 'print') setProgDownloadingPrint(false);
      else setProgDownloadingScreen(false);
    }
  };

  const hasSettings = !!progSettings;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-2">Final Programme</h3>
        <p className="text-sm text-muted-foreground">
          Generate the final competition programme in versions optimised for screen or print.
        </p>
      </div>

      {finalProgComp && (
        <ReconcileWarningBanner competitionId={finalProgComp} />
      )}

      {!finalProgComp && (
        <div className="rounded-lg border bg-stone-50 px-3 py-3 text-sm text-stone-600">
          Pick a competition above to continue.
          {competitionsWithOOS.length === 0 && (
            <span className="block text-xs text-amber-600 mt-1">
              No competitions with a generated Order of Skate found.
            </span>
          )}
        </div>
      )}

      {finalProgComp && !competitionsWithOOS.find(c => c.competition_id === finalProgComp) && (
        <div className="rounded-lg border bg-amber-50 px-3 py-3 text-sm text-amber-900">
          This competition doesn't have an Order of Skate yet. Generate one first.
        </div>
      )}

      {finalProgComp && competitionsWithOOS.find(c => c.competition_id === finalProgComp) && (
        <>
          {/* Draw-required gate: Local/Regional/Area/National comps need
              the Skating Order Draw before the Final Programme can be
              generated. International / World / Invitational stay open. */}
          {drawBlocking && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
              data-testid="final-prog-draw-required-banner"
            >
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">
                  Skating Order Draw not yet completed.
                </p>
                <p className="text-xs mt-1">
                  The Final Programme can&apos;t be generated for {compType} competitions until the draw has been completed.
                  Open the <span className="font-medium">Competition Draws</span> tile to complete it.
                </p>
              </div>
            </div>
          )}

          {/* TILE 1: Programme settings */}
          <Card className="p-5 space-y-4 border-stone-200 bg-stone-50/50" data-testid="final-prog-settings-tile">
            <div>
              <h4 className="text-base font-semibold text-stone-800">Final Programme settings</h4>
              <p className="text-sm text-stone-600 mt-1">
                Provide the cover, oaths placement, acknowledgements, volunteers and sponsors used in the final document.
              </p>
            </div>
            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                onClick={() => setProgDialogOpen(true)}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                data-testid="open-prog-settings-btn"
              >
                <Settings className="mr-2 h-4 w-4" />
                Collect Information
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">Information for Final Programme</p>
                <p className="text-xs text-stone-600">
                  {hasSettings ? 'Settings saved.' : 'No information saved yet — click to begin.'}
                </p>
              </div>
            </div>
          </Card>

          {/* TILE 2: Generate Final Programme — download only */}
          <Card className="p-5 space-y-4 border-stone-200 bg-stone-50/50" data-testid="final-prog-generate-tile">
            <div>
              <h4 className="text-base font-semibold text-stone-800">Create Final Programme for Download</h4>
            </div>
            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                onClick={() => downloadFinalProgramme('print')}
                disabled={progDownloadingPrint || drawBlocking}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                data-testid="final-print-btn"
              >
                <Download className={`mr-2 h-4 w-4 ${progDownloadingPrint ? 'animate-pulse' : ''}`} />
                {progDownloadingPrint ? 'Generating...' : 'Final Programme (Print)'}
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">Generate final for print and download</p>
                <p className="text-xs text-stone-600">Generates the final programme optimised for printing.</p>
              </div>
            </div>
            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                onClick={() => downloadFinalProgramme('screen')}
                disabled={progDownloadingScreen || drawBlocking}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                data-testid="final-screen-btn"
              >
                <Download className={`mr-2 h-4 w-4 ${progDownloadingScreen ? 'animate-pulse' : ''}`} />
                {progDownloadingScreen ? 'Generating...' : 'Final Programme (Screen)'}
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">Generate final for screen and download</p>
                <p className="text-xs text-stone-600">Generates the final programme optimised for screen use.</p>
              </div>
            </div>
          </Card>

          {/* TILE 3: Email Final Programme to Clubs */}
          <Card className="p-5 space-y-4 border-stone-200 bg-stone-50/50" data-testid="fp-email-tile">
            <div>
              <h4 className="text-base font-semibold text-stone-800">Email Final Programme to Clubs</h4>
              <p className="text-sm text-stone-600 mt-1">
                Generate final programmes for clubs and send out by email.
              </p>
              <LastSentLine competitionId={finalProgComp} kind="final-programme" />
            </div>

            <div>
              <Label htmlFor="fp-club-select" className="text-sm font-medium text-stone-700">
                Clubs (Select individual clubs or All Clubs Option)
              </Label>
              <select
                id="fp-club-select"
                className="w-full border rounded-lg px-3 py-2 mt-1 text-sm"
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                data-testid="fp-club-select"
              >
                <option value="all">All Clubs ({clubsWithEntries.length} clubs)</option>
                {clubsWithEntries.map((c) => (
                  <option key={c.club_id} value={c.club_id}>
                    {c.name}{c.missing_email ? ' (no email on file)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <h5 className="text-sm font-semibold text-stone-800 mb-2">Attachments</h5>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-stone-800">
                  <input
                    type="checkbox"
                    checked={emailVersions.print}
                    onChange={(e) => setEmailVersions(v => ({ ...v, print: e.target.checked }))}
                    data-testid="fp-attach-print-checkbox"
                  />
                  Attach Final Programme (Optimised for print)
                </label>
                <label className="flex items-center gap-2 text-sm text-stone-800">
                  <input
                    type="checkbox"
                    checked={emailVersions.screen}
                    onChange={(e) => setEmailVersions(v => ({ ...v, screen: e.target.checked }))}
                    data-testid="fp-attach-screen-checkbox"
                  />
                  Attach Final Programme (Optimised for screen)
                </label>
              </div>
            </div>

            <Button
              onClick={() => setEmailDialogOpen(true)}
              disabled={(!emailVersions.print && !emailVersions.screen) || drawBlocking}
              className="btn-primary"
              data-testid="email-final-programmes-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Email Final Programmes
            </Button>
          </Card>

          {/* TILE 4: Publish Programme PDFs to Competition Page (only when a page is approved) */}
          {publishedPageId && (
            <Card className="p-5 space-y-4 border-stone-200 bg-stone-50/50" data-testid="fp-publish-tile">
              <div>
                <h4 className="text-base font-semibold text-stone-800">Publish Programme PDFs to Competition Page</h4>
                {lastPublishedAt && (
                  <p className="text-xs text-stone-500 mt-1" data-testid="fp-last-published-line">
                    Last published: {(() => {
                      const dt = new Date(lastPublishedAt);
                      const date = dt.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
                      const time = dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false });
                      return `${date} at ${time}`;
                    })()} — {lastPublishTypes.length === 2 ? 'both versions' : (lastPublishTypes[0] === 'programme_screen' ? 'Screen only' : 'Print only')}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
                <Button
                  onClick={handlePublishProgrammes}
                  disabled={publishingProgrammes || drawBlocking}
                  className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                  data-testid="publish-programmes-btn"
                >
                  <Upload className={`mr-2 h-4 w-4 ${publishingProgrammes ? 'animate-pulse' : ''}`} />
                  {publishingProgrammes ? 'Publishing…' : 'Publish Programmes'}
                </Button>
                <div>
                  <p className="text-sm font-medium text-stone-800">Generate PDFs of both programmes for competition page</p>
                  <p className="text-xs text-stone-600">
                    Attaches both versions of the programme to the published Competition Page.
                  </p>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* === Programme Settings Dialog === */}
      <AlertDialog open={progDialogOpen} onOpenChange={setProgDialogOpen}>
        <AlertDialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="prog-settings-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Final Programme — Information</AlertDialogTitle>
            <AlertDialogDescription>
              Configure the cover, oaths, acknowledgements, volunteers and sponsors for this competition.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {progSettings && (
            <div className="space-y-6 mt-2">
              {/* Cover Page */}
              <section className="space-y-2">
                <h4 className="font-semibold text-stone-800">Cover Page</h4>
                <div className="flex flex-wrap gap-4 items-center">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="cover_type"
                      checked={progSettings.cover_type === 'text'}
                      onChange={() => updateProgSettings({ cover_type: 'text' })}
                    />
                    Text-based cover
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="cover_type"
                      checked={progSettings.cover_type === 'image'}
                      onChange={() => updateProgSettings({ cover_type: 'image' })}
                    />
                    Upload an image
                  </label>
                </div>
                {progSettings.cover_type === 'image' && (
                  <div className="flex flex-wrap gap-3 items-center">
                    <label className={`inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium cursor-pointer hover:bg-accent ${progUploadingCover ? 'opacity-60 pointer-events-none' : ''}`}>
                      <Upload className="mr-2 h-4 w-4" />
                      {progUploadingCover ? 'Uploading...' : (progSettings.cover_image_path ? 'Replace Image' : 'Upload Image (A4)')}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadCoverImage(f); e.target.value = ''; } }}
                      />
                    </label>
                    {progSettings.cover_image_filename && (
                      <span className="text-sm text-stone-700">
                        {progSettings.cover_image_filename}
                        <Button variant="link" className="text-red-600" onClick={removeCoverImage}>Remove</Button>
                      </span>
                    )}
                  </div>
                )}
              </section>

              {/* Clubs list toggle */}
              <section className="space-y-2">
                <h4 className="font-semibold text-stone-800">Acknowledgement of Clubs</h4>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={progSettings.include_clubs_list ?? true}
                    onChange={(e) => updateProgSettings({ include_clubs_list: e.target.checked })}
                  />
                  Yes, list the clubs that have entered skaters in this competition.
                </label>
              </section>

              {/* Oaths placement */}
              <section className="space-y-2">
                <h4 className="font-semibold text-stone-800">Skater &amp; Judges Oaths</h4>
                <div className="flex flex-wrap gap-4 items-center">
                  {[
                    { v: 'front', label: 'Front (after welcome page)' },
                    { v: 'back', label: 'Back (after sponsors)' },
                    { v: 'none', label: "Don't include" },
                  ].map(opt => (
                    <label key={opt.v} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="oaths_position"
                        checked={progSettings.oaths_position === opt.v}
                        onChange={() => updateProgSettings({ oaths_position: opt.v })}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </section>

              {/* Oath text — customisable per federation */}
              <section className="space-y-2">
                <h4 className="font-semibold text-stone-800">Oath text</h4>
                <p className="text-xs text-stone-600">
                  Different countries use different wording. Edit the title or body for either oath as needed.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Skaters' Oath title</Label>
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2"
                      value={progSettings.skaters_oath_title || ''}
                      onChange={(e) => updateProgSettings({ skaters_oath_title: e.target.value })}
                      data-testid="skaters-oath-title-input"
                    />
                    <Label>Skaters' Oath text</Label>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 min-h-[180px]"
                      value={progSettings.skaters_oath_text || ''}
                      onChange={(e) => updateProgSettings({ skaters_oath_text: e.target.value })}
                      data-testid="skaters-oath-text-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Officials' Oath title</Label>
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2"
                      value={progSettings.officials_oath_title || ''}
                      onChange={(e) => updateProgSettings({ officials_oath_title: e.target.value })}
                      data-testid="officials-oath-title-input"
                    />
                    <Label>Officials' Oath text</Label>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 min-h-[180px]"
                      value={progSettings.officials_oath_text || ''}
                      onChange={(e) => updateProgSettings({ officials_oath_text: e.target.value })}
                      data-testid="officials-oath-text-input"
                    />
                  </div>
                </div>
              </section>

              {/* Volunteers */}
              <section className="space-y-2">
                <h4 className="font-semibold text-stone-800">Volunteers</h4>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Name" className="border rounded-lg px-3 py-2"
                          value={progNewVolName} onChange={(e) => setProgNewVolName(e.target.value)} />
                  <input type="text" placeholder="Role / What they did" className="border rounded-lg px-3 py-2"
                          value={progNewVolRole} onChange={(e) => setProgNewVolRole(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  {progSelectedVolIdx === null ? (
                    <Button size="sm" onClick={addVolunteer}>Add</Button>
                  ) : (
                    <>
                      <Button size="sm" onClick={updateVolunteer}>Update</Button>
                      <Button size="sm" variant="destructive" onClick={deleteVolunteer}>Delete</Button>
                      <Button size="sm" variant="outline" onClick={() => { setProgSelectedVolIdx(null); setProgNewVolName(''); setProgNewVolRole(''); }}>Cancel</Button>
                    </>
                  )}
                </div>
                {(progSettings.volunteers || []).length > 0 && (
                  <ul className="border rounded-lg divide-y mt-2">
                    {(progSettings.volunteers || []).map((v, i) => (
                      <li key={i}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-stone-100 ${progSelectedVolIdx === i ? 'bg-stone-100' : ''}`}
                          onClick={() => selectVolunteer(i)}>
                        <span className="font-medium">{v.name}</span>
                        {v.role && <span className="text-stone-600"> — {v.role}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Sponsors */}
              <section className="space-y-2">
                <h4 className="font-semibold text-stone-800">Sponsors</h4>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Sponsor name" className="border rounded-lg px-3 py-2"
                          value={progNewSpName} onChange={(e) => setProgNewSpName(e.target.value)} />
                  <input type="text" placeholder="Website or phone" className="border rounded-lg px-3 py-2"
                          value={progNewSpContact} onChange={(e) => setProgNewSpContact(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  {progSelectedSpIdx === null ? (
                    <Button size="sm" onClick={addSponsor}>Add</Button>
                  ) : (
                    <>
                      <Button size="sm" onClick={updateSponsor}>Update</Button>
                      <Button size="sm" variant="destructive" onClick={deleteSponsor}>Delete</Button>
                      <Button size="sm" variant="outline" onClick={() => { setProgSelectedSpIdx(null); setProgNewSpName(''); setProgNewSpContact(''); }}>Cancel</Button>
                    </>
                  )}
                </div>
                {(progSettings.sponsors || []).length > 0 && (
                  <ul className="border rounded-lg divide-y mt-2">
                    {(progSettings.sponsors || []).map((s, i) => (
                      <li key={i}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-stone-100 ${progSelectedSpIdx === i ? 'bg-stone-100' : ''}`}
                          onClick={() => selectSponsor(i)}>
                        <span className="font-medium">{s.name}</span>
                        {s.contact && <span className="text-stone-600"> — {s.contact}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Acknowledgements text — appears after Sponsors per user feedback */}
              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-stone-800">Acknowledgements page</h4>
                  <Button variant="outline" size="sm" onClick={handleGenerateDraftClick}>
                    Generate Draft Text
                  </Button>
                </div>
                <p className="text-xs text-stone-600">
                  The Welcome Letter on the final document. The Competition's Host Organisation is automatically added below the sign-off as the signing entity.
                </p>
                <div>
                  <Label>Page Header</Label>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    value={progSettings.page_header || ''}
                    onChange={(e) => updateProgSettings({ page_header: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Page Text</Label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 mt-1 min-h-[150px]"
                    value={progSettings.page_text || ''}
                    onChange={(e) => updateProgSettings({ page_text: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Sign-off</Label>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    value={progSettings.sign_off || ''}
                    onChange={(e) => updateProgSettings({ sign_off: e.target.value })}
                  />
                </div>
              </section>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); saveProgSettings(); }}
              disabled={progSavingSettings}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
            >
              {progSavingSettings ? 'Saving...' : 'Save Settings'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === Generate Draft Text confirmation === */}
      <AlertDialog open={progGenDraftConfirmOpen} onOpenChange={setProgGenDraftConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Replace existing acknowledgements text?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The Page Header, Page Text and Sign-off currently contain content. Generating draft text will replace these. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setProgGenDraftConfirmOpen(false); generateDraftAcknowledgements(); }}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CoveringEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        competitionId={finalProgComp}
        clubId={selectedClub}
        endpointBase="final-programme-email"
        title="Email Final Programmes to Clubs"
        description="Compose the covering email. The selected Final Programme .docx file(s) will be attached to every email."
        testIdPrefix="fp-email"
        extraSendBody={{
          versions: [
            ...(emailVersions.print ? ['print'] : []),
            ...(emailVersions.screen ? ['screen'] : []),
          ],
        }}
      />
    </div>
  );
}
